import React from 'react';
import type { Project } from 'types/api.types';
import ControlledSaveDiscardButtons from 'components/common/form-controlled/controlled-save-discard-buttons';
import { useProjectGeneralSettingsForm } from 'components/projects/settings/general/project-settings-general-form.hook';
import ControlledTextInput from 'components/common/form-controlled/controlled-text-input';
import ControlledTextArea from 'components/common/form-controlled/controlled-text-area';
import Card from 'components/common/card';
import PageTitle from 'components/common/page-title';
import { ArchiveProjectButton } from 'components/projects/settings/archive-project-button';
import { Col, Row } from 'components/common/grid';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { useIntl } from 'react-intl';
import { CopyableIdWithLabel } from 'components/common/copyable-id';
import { MoveProjectButton } from 'components/projects/settings/move-project-button';

export interface ProjectGeneralUsersProp {
  project: Project;
}

const ProjectGeneralUsers: React.FC<ProjectGeneralUsersProp> = ({ project }) => {
  const theme = useTheme();
  const form = useProjectGeneralSettingsForm(project);
  const intl = useIntl();
  const title = intl.formatMessage({ id: 'projects.settings.menu.general' });

  // Figma 254:32132: an H2 pane title with Archive (ghost danger) and Move (primary) beside it, then
  // one card holding the fields and the save row alone. Legacy keeps everything inside its card.
  if (theme.isNewDesign) {
    return (
      <PaneStack>
        <PageTitle
          titleId="projects.settings.general.title"
          data-e2e="project-settings-general-title"
          actions={
            <>
              <ArchiveProjectButton isSubmitting={form.isSubmitting} project={project} />
              <MoveProjectButton id={project.id} name={project.name} />
            </>
          }
        />
        <Card>
          {/* Plain id text with its copy glyph - the frame drops the code chip (254:32137). */}
          <CopyableIdWithLabel
            text={project.id}
            label={{ id: 'projects.settings.general.id.label' }}
            entityName={'project'}
          />

          <ControlledTextInput label={{ id: 'projects.settings.general.name.label' }} name="name" form={form} />
          <ControlledTextArea
            label={{ id: 'projects.settings.general.description.label', optional: true }}
            placeholder={intl.formatMessage({ id: 'projects.settings.general.description.placeholder' })}
            name="description"
            form={form}
          />
          <SaveRow>
            <ControlledSaveDiscardButtons form={form} cancelMessageId={'discard.changes'} inContainer={true} />
          </SaveRow>
        </Card>
      </PaneStack>
    );
  }

  return (
    <Card title={<>{title}</>}>
      <CopyableIdWithLabel text={project.id} label={{ id: 'id' }} showCodeStyle entityName={'project'} />

      <ControlledTextInput label={{ id: 'name' }} name="name" form={form} />
      <ControlledTextArea label={{ id: 'description', optional: true }} name="description" form={form} />
      <StyledRow>
        <StyledButtonsContainer>
          <ArchiveProjectButton isSubmitting={form.isSubmitting} project={project} />
          <LeftMarginContainer>
            <MoveProjectButton id={project.id} name={project.name} />
          </LeftMarginContainer>
        </StyledButtonsContainer>
        <Col>
          <ControlledSaveDiscardButtons form={form} cancelMessageId={'discard.changes'} inContainer={true} />
        </Col>
      </StyledRow>
    </Card>
  );
};

const PaneStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// 1vw was viewport-relative for no reason anyone recorded — it lands at 14px on a 1440 frame, so the
// new design takes the nearest step on the scale and stops moving with the window.
const LeftMarginContainer = styled.div`
  ${legacyOnly(css`
    margin-left: 1vw;
  `)}

  ${newDesignOnly(css`
    margin-left: var(--spacing-lg);
  `)}
`;

const StyledButtonsContainer = styled(Col)`
  display: flex;
`;

const StyledRow = styled(Row)`
  justify-content: space-between;
  align-items: baseline;
`;

export default ProjectGeneralUsers;
